// SessionManager.js
// Utility functions for managing multiple sessions per browser

/**
 * Get the current tab's session ID
 * Uses sessionStorage so each tab has its own session
 */
export const getCurrentSessionId = () => {
  return sessionStorage.getItem('current_session_id');
};

/**
 * Set the current tab's session ID
 */
export const setCurrentSessionId = (sessionId) => {
  sessionStorage.setItem('current_session_id', sessionId);
};

/**
 * Get the token for the current tab's session.
 * In embed mode, returns the embed token from sessionStorage.
 */
export const getCurrentToken = () => {
  // Embed mode — token is stored in sessionStorage by EmbedView
  if (sessionStorage.getItem('embed_mode') === 'true') {
    return sessionStorage.getItem('embed_token');
  }

  const sessionId = getCurrentSessionId();
  if (!sessionId) return null;
  
  return localStorage.getItem(`auth_token_${sessionId}`);
};

/**
 * Save token for a specific session
 */
export const saveSessionToken = (sessionId, token) => {
  localStorage.setItem(`auth_token_${sessionId}`, token);
  setCurrentSessionId(sessionId);
};

/**
 * Remove token for current session (logout current tab).
 * No-op in embed mode — embed lifecycle is managed by the host app.
 */
export const removeCurrentSession = () => {
  if (sessionStorage.getItem('embed_mode') === 'true') return;

  const sessionId = getCurrentSessionId();
  if (sessionId) {
    localStorage.removeItem(`auth_token_${sessionId}`);
    sessionStorage.removeItem('current_session_id');
  }
};

/**
 * Get all session IDs stored in localStorage
 */
export const getAllSessionIds = () => {
  const sessions = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('auth_token_')) {
      const sessionId = key.replace('auth_token_', '');
      sessions.push(sessionId);
    }
  }
  return sessions;
};

/**
 * Clean up invalid sessions
 * Call this periodically or on login
 */
export const cleanupInvalidSessions = async (validateTokenFn) => {
  const allSessions = getAllSessionIds();
  
  for (const sessionId of allSessions) {
    const token = localStorage.getItem(`auth_token_${sessionId}`);
    if (token) {
      try {
        const isValid = await validateTokenFn(token);
        if (!isValid) {
          localStorage.removeItem(`auth_token_${sessionId}`);
        }
      } catch (error) {
        // If validation fails, remove the session
        localStorage.removeItem(`auth_token_${sessionId}`);
      }
    }
  }
};

/**
 * Get all active sessions info
 * Returns array of { sessionId, token }
 */
export const getAllActiveSessions = () => {
  const sessions = getAllSessionIds();
  return sessions.map(sessionId => ({
    sessionId,
    token: localStorage.getItem(`auth_token_${sessionId}`)
  }));
};

/**
 * Check if current tab has an active session
 */
export const hasActiveSession = () => {
  if (sessionStorage.getItem('embed_mode') === 'true') return true;
  return getCurrentToken() !== null;
};

export default {
  getCurrentSessionId,
  setCurrentSessionId,
  getCurrentToken,
  saveSessionToken,
  removeCurrentSession,
  getAllSessionIds,
  cleanupInvalidSessions,
  getAllActiveSessions,
  hasActiveSession
};